// Phase 8A/DA1 — Zone C Live Intelligence Rail.
//
// Design Doc §2.4, DA1.2, DA1.3: 300px fixed panel showing live context:
// Goal Progress, Bankroll State, Chain Position.
// Fed by the same data as state injection — no separate API calls.
// Shimmer skeletons during load (DA1.2). No honesty rules (DA1.3).
// Hidden below 1200px (minimised to 32px tab per §10).

import { useEffect, useId, useMemo, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useSikaGoals } from "../../sika/useSikaGoals.ts";
import { useRolloverChain } from "../../vault/useRolloverChain.ts";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

const fmtAmt = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** Shimmer skeleton block — renders while data is loading (DA1.2). */
function Shimmer({ height = 14, width = "100%", style }: {
  height?: number;
  width?: string | number;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div
      className="ctx-shimmer"
      style={{ height, width: typeof width === "number" ? `${width}px` : width, ...style }}
    />
  );
}

/** Shimmer skeleton for a full block. */
function ShimmerBlock({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="ctx-shimmer-block">
      <Shimmer width={100} height={10} style={{ marginBottom: 12 }} />
      {Array.from({ length: rows }).map((_, i) => (
        <Shimmer key={i} height={14} width={i === 0 ? "80%" : "60%"} style={{ marginBottom: 8 }} />
      ))}
    </div>
  );
}

interface ContextPaneProps {
  /** When provided, the pane shows this goal instead of the first active one. */
  goalId?: string | null;
}

export function SikaContextPane({ goalId }: ContextPaneProps): JSX.Element {
  const { goals, loading: goalsLoading } = useSikaGoals();
  const { chain, loading: chainLoading } = useRolloverChain();
  const { user } = useAuthSession();
  const supabase = useMemo(() => getBrowserClient(), []);
  const instanceId = useId();

  // Bankroll state — fetch latest balance from bankroll_events
  const [bankroll, setBankroll] = useState<{
    available: number | null;
    loading: boolean;
  }>({ available: null, loading: true });

  useEffect(() => {
    if (!user) { setBankroll({ available: null, loading: false }); return; }
    const fetchBankroll = async () => {
      const { data } = await supabase
        .from("bankroll_events")
        .select("balance_after")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      const bal = data?.[0]?.balance_after ?? null;
      setBankroll({ available: bal, loading: false });
    };
    void fetchBankroll();
  }, [supabase, user]);

  // Realtime subscription for chain updates (DA1.2)
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`zone-c-chains:${instanceId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "rollover_chains",
        filter: `user_id=eq.${user.id}`,
      }, () => {
        // Chain hook will refetch via its own subscription
        // but we ensure bankroll also refreshes
        void (async () => {
          const { data } = await supabase
            .from("bankroll_events")
            .select("balance_after")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1);
          setBankroll({ available: data?.[0]?.balance_after ?? null, loading: false });
        })();
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [supabase, user, instanceId]);

  // When goalId is provided, show that specific goal; otherwise fall back
  // to the first active goal (original behaviour).
  const activeGoal = goalId
    ? goals.find((g) => g.id === goalId) ??
      goals.find((g) => g.status === "active" || g.status === "in_progress")
    : goals.find(
        (g) => g.status === "active" || g.status === "in_progress",
      );
  const activeGoalCount = goals.filter(
    (g) => g.status === "active" || g.status === "in_progress",
  ).length;

  const progress = activeGoal
    ? activeGoal.target_amount > 0
      ? Math.min(100, Math.round((activeGoal.current_amount / activeGoal.target_amount) * 100))
      : 0
    : 0;

  const isAchieved = activeGoal?.status === "achieved" || activeGoal?.achieved_at != null;

  // Chain progress
  const chainWeeks = activeGoal?.timeline_weeks ?? 0;
  const chainProgress = chain && chainWeeks > 0
    ? Math.min(100, Math.round((chain.current_week / chainWeeks) * 100))
    : 0;

  // Pace indicator
  const expectedProgress = activeGoal?.timeline_weeks
    ? Math.round((chain?.current_week ?? 0) / activeGoal.timeline_weeks * 100)
    : 0;
  const isOnPace = progress >= expectedProgress - 5;

  // Safe mode: if takeout_pct > 0 the user has protection enabled
  const safeMode = activeGoal ? activeGoal.takeout_pct > 0 : false;

  const isLoading = goalsLoading || chainLoading || bankroll.loading;

  return (
    <div className="ctx-pane">
      {/* ─── Goal Progress Block ─── */}
      <div className="ctx-block">
        <div className="ctx-block-label">
          {!isLoading && activeGoal && <span className="ctx-active-dot" />}
          Goal progress
        </div>

        {isLoading ? (
          <ShimmerBlock rows={4} />
        ) : isAchieved && activeGoal ? (
          <div className="ctx-goal-achieved">
            <div className="ctx-goal-name">{activeGoal.title}</div>
            <div className="ctx-achieved-badge">
              <Icon name="check" size={12} />
              Goal reached
            </div>
            <div className="ctx-amount" style={{ color: "var(--sika-gold-100)" }}>
              GH₵ {fmtAmt(activeGoal.target_amount)}
            </div>
          </div>
        ) : activeGoal ? (
          <>
            <div className="ctx-goal-name">{activeGoal.title}</div>
            <div className="ctx-progress-bar">
              <div className="ctx-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="ctx-amount">
              <span>GH₵ {fmtAmt(activeGoal.current_amount)}</span>{" "}
              <span className="ctx-amount-target">of GH₵ {fmtAmt(activeGoal.target_amount)}</span>
            </div>
            {activeGoal.timeline_weeks != null && chain && (
              <div className="ctx-status-line">
                Week {chain.current_week} of {activeGoal.timeline_weeks} · {progress}% to goal
              </div>
            )}
            <div className="ctx-pace">
              <span className={`pace-dot ${isOnPace ? "on-pace" : "behind"}`} />
              {isOnPace ? "On pace" : "Behind schedule"}
              {activeGoal.timeline_weeks != null && chain && (
                <span> · Est. {Math.max(0, activeGoal.timeline_weeks - chain.current_week)} weeks remaining</span>
              )}
            </div>
            {activeGoalCount > 1 && (
              <div className="ctx-see-all">
                See all goals ({activeGoalCount})
              </div>
            )}
          </>
        ) : (
          <div className="ctx-empty-hint">
            No active goal — start one below.
          </div>
        )}
      </div>

      {/* ─── Bankroll State Block ─── */}
      <div className="ctx-block">
        <div className="ctx-block-label">Bankroll state</div>

        {isLoading ? (
          <ShimmerBlock rows={4} />
        ) : (
          <>
            <div className="ctx-stat-row">
              <span className="ctx-stat-label">Available</span>
              <span className="ctx-stat-value">
                {bankroll.available != null
                  ? `GH₵ ${fmtAmt(bankroll.available)}`
                  : "Not set"}
              </span>
            </div>
            <div className="ctx-stat-row">
              <span className="ctx-stat-label">Drawdown guard</span>
              <span className={`ctx-stat-value ${chain?.status === "bust" ? "warning" : "clear"}`}>
                {chain?.status === "bust" ? (
                  <><Icon name="alert" size={11} /> Active</>
                ) : (
                  <><Icon name="check" size={11} /> Clear</>
                )}
              </span>
            </div>
            <div className="ctx-stat-row">
              <span className="ctx-stat-label">Safe Mode</span>
              <span className={`ctx-stat-value ${safeMode ? "clear" : ""}`}>
                {safeMode
                  ? `On · ${((activeGoal?.takeout_pct ?? 0.5) * 100).toFixed(0)}% takeout`
                  : "Off"}
              </span>
            </div>
            <div className="ctx-stat-row">
              <span className="ctx-stat-label">Risk tolerance</span>
              <span className="ctx-stat-value">
                {activeGoal?.risk_tolerance ?? "Not set"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ─── Chain Position Block ─── */}
      <div className="ctx-block">
        <div className="ctx-block-label">Chain position</div>

        {isLoading ? (
          <ShimmerBlock rows={3} />
        ) : chain && chain.status !== "bust" ? (
          <>
            <div className="ctx-chain-track">
              <div className="ctx-chain-bg" />
              <div className="ctx-chain-fill" style={{ width: `${chainProgress}%` }} />
              <div className="ctx-chain-dot" style={{ left: `${chainProgress}%` }} />
            </div>
            <div className="ctx-chain-labels">
              <span>Week {chain.current_week}</span>
              <span>{chainWeeks > 0 ? `Week ${chainWeeks}` : "Target"}</span>
            </div>
            <div className="ctx-stat-row" style={{ marginTop: 12 }}>
              <span className="ctx-stat-label">Current value</span>
              <span className="ctx-stat-value">GH₵ {fmtAmt(chain.current_value)}</span>
            </div>
            {activeGoal?.protected_amount != null && (
              <div className="ctx-stat-row">
                <span className="ctx-stat-label">Protected banked</span>
                <span className="ctx-stat-value clear">GH₵ {fmtAmt(activeGoal.protected_amount)}</span>
              </div>
            )}
          </>
        ) : chain?.status === "bust" ? (
          <div className="ctx-chain-bust">
            <div className="ctx-chain-track">
              <div className="ctx-chain-bg" />
              <div className="ctx-chain-fill bust" style={{ width: `${chainProgress}%` }} />
            </div>
            <div className="ctx-chain-labels">
              <span>Start</span>
              <span>Bust at Week {chain.current_week}</span>
            </div>
            <div className="ctx-chain-bust-label">
              Chain bust — restart available
            </div>
          </div>
        ) : (
          <div className="ctx-empty-hint">
            No chain running. Activate a plan to begin.
          </div>
        )}
      </div>
    </div>
  );
}
